import { useQuery } from "@apollo/client/react";
import { Box, Flex, Text } from "@nulogy/components";
import HalfField from "components/common/HalfField";
import Section from "components/common/Section";
import DatePicker from "components/controls/DatePicker";
import Form from "components/controls/Form";
import LockToggle from "components/controls/LockToggle";
import ModalFormButtons from "components/controls/ModalFormButtons";
import Select from "components/controls/Select";
import { formatDuration } from "components/helpers/format";
import BlockModal from "components/pages/schedule/styles/BlockModal";
import DowntimeBlock from "domain/DowntimeBlock";
import useRescheduleBlocks from "hooks/apollo/blocks/useRescheduleBlocks";
import { GetDowntimeReasons } from "hooks/apollo/downtimeReason/downtimeReason.gql";
import { GetAllLines } from "hooks/apollo/line/line.gql";
import useDowntimeEditModal from "hooks/state/useDowntimeEditModal";
import React, { useState } from "react";
import DowntimeReasonSelect from "./DowntimeReasonSelect";

export default function EditDowntimeBlockModal() {
  const { block: blockData, isOpen, close } = useDowntimeEditModal();

  const { data: linesData, loading } = useQuery(GetAllLines, { variables: { activeOnly: true } });
  const { data: downtimeReasonsData, loading: downtimeReasonsLoading } = useQuery(GetDowntimeReasons);

  const rescheduleBlocks = useRescheduleBlocks();
  const [errors, setErrors] = useState({});

  if (!blockData || loading || downtimeReasonsLoading) return null;

  const { block } = blockData;
  const { lines } = linesData;

  const initialValues = block;

  let { overlappingPlannedBreaks } = initialValues;

  const validate = (data) => {
    const validationErrors = DowntimeBlock.validate(data, downtimeReasonsData.downtimeReasons.length);

    setErrors(validationErrors);

    return validationErrors;
  };

  const handleSave = (data) =>
    new Promise(() => {
      rescheduleBlocks([{ ...data, __typename: "DowntimeBlock" }]);
      close();
    });

  const lineOptions = lines.map((line) => ({ label: line.name, value: line.id }));

  const handleFormChange = (e) => {
    const downtimeBlock = e.values;

    if (downtimeBlock) {
      const newLine = lines.find((line) => line.id === downtimeBlock.line.id);
      overlappingPlannedBreaks = DowntimeBlock.updateOverlappingPlannedBreaks({
        ...downtimeBlock,
        line: newLine,
      }).overlappingPlannedBreaks;
    }
  };

  return (
    <BlockModal
      className="spec-downtime-edit-modal"
      footerContent={
        <ModalFormButtons
          disabled={Object.keys(errors).length > 0}
          formId="editDowntime"
          onClose={close}
          pendoTagClass="pendo-edit-downtime-modal-save"
        />
      }
      isOpen={isOpen}
      onRequestClose={close}
      title="Edit downtime block"
    >
      <Form
        id="editDowntime"
        initialValues={initialValues}
        onChange={handleFormChange}
        onSave={handleSave}
        validate={validate}
      >
        {({ values: { lockedOnSchedule, reason, reasonCode } }) => (
          <Section controls={<LockToggle field="lockedOnSchedule" />} height="100%" mb={0} title="Scheduling details">
            <DowntimeReasonSelect
              downtimeBlock={{ reason, reasonCode }}
              downtimeReasonsData={downtimeReasonsData}
              downtimeReasonsLoading={downtimeReasonsLoading}
              lockedOnSchedule={lockedOnSchedule}
            />
            <Box data-testid="line-select" marginBottom="x2">
              <Select
                className="spec-line-select"
                disabled={lockedOnSchedule}
                field="line[id]"
                labelText="Line"
                maxHeight="250"
                options={lineOptions}
              />
            </Box>
            <Box marginBottom="x2">
              <Flex className="customDatePicker">
                <Box marginRight="x3" width={1 / 2}>
                  <DatePicker
                    className="spec-startsAt"
                    disabled={lockedOnSchedule}
                    field="startsAt"
                    labelText="Starts"
                  />
                </Box>
                <Box data-testid="ends-at" marginLeft="x3" width={1 / 2}>
                  <DatePicker className="spec-endsAt" disabled={lockedOnSchedule} field="endsAt" labelText="Ends" />
                </Box>
              </Flex>
            </Box>
            {overlappingPlannedBreaks.length > 0 && (
              <HalfField labelText="Planned breaks" mb="0px">
                {overlappingPlannedBreaks.map(({ plannedBreakName, duration }) => (
                  <Text key={plannedBreakName}>{`${plannedBreakName}: ${formatDuration(duration)}`}</Text>
                ))}
              </HalfField>
            )}
          </Section>
        )}
      </Form>
    </BlockModal>
  );
}
